import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';

interface Template { readonly id: string; readonly name: string; readonly channel: string; readonly subject: string; readonly body: string; }

export function NotificationsTemplateEditPage(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState<Template | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<Template>(`/notifications/templates/${id}`, { token })
      .then((r) => { if (alive) setData(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [id, token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!data) return;
    try {
      await apiRequest(`/notifications/templates/${id}`, {
        method: 'PUT', token, body: { subject: data.subject, body: data.body },
      });
      setSaved(true);
    } catch (err) { setError((err as Error).message); }
  }

  async function setActive(active: boolean) {
    setStatusMsg(null);
    try {
      await apiRequest(`/notifications/templates/${id}/${active ? 'activate' : 'deactivate'}`, {
        method: 'POST', token,
      });
      setStatusMsg(active ? t('notif.templateEdit.activated') : t('notif.templateEdit.deactivated'));
    } catch (err) { setError((err as Error).message); }
  }

  if (error && !data) return <p role="alert">{error}</p>;
  if (!data) return <p>{t('notif.common.loading')}</p>;
  return (
    <main aria-label="template-edit">
      <h1>{data.name}</h1>
      <p aria-label="template-channel">{t('notif.templateEdit.channelLabel', { channel: data.channel })}</p>
      <form onSubmit={onSubmit} aria-label="template-form">
        <label htmlFor="t-subject">{t('notif.common.subject')}</label>
        <input id="t-subject" value={data.subject} onChange={(e) => setData({ ...data, subject: e.target.value })} />
        <label htmlFor="t-body">{t('notif.common.body')}</label>
        <textarea id="t-body" value={data.body} onChange={(e) => setData({ ...data, body: e.target.value })} />
        {error ? <p role="alert">{error}</p> : null}
        {saved ? <p role="status">{t('notif.templateEdit.saved')}</p> : null}
        <Button type="submit" variant="primary">{t('notif.templateEdit.save')}</Button>
      </form>
      <div aria-label="template-status-actions">
        <Button type="button" onClick={() => void setActive(true)} variant="secondary">{t('notif.templateEdit.activate')}</Button>
        <Button type="button" onClick={() => void setActive(false)} variant="danger">{t('notif.templateEdit.deactivate')}</Button>
        {statusMsg ? <p role="status">{statusMsg}</p> : null}
      </div>
    </main>
  );
}
